import { migrateReadingLevel, migrateWritingLevel } from '@skjv/common';
import type { Student, Study, StudyGroup } from '@skjv/common';

/**
 * In-memory migration of legacy reading/writing level values on load and
 * import. Loads are raw JSON casts (no Zod), so old-enum — or level-less —
 * student records reach state untouched; mapping them here keeps every
 * consumer on the current enums. Idempotent, and deliberately NOT written
 * back eagerly: saves are scoped to studies whose updatedAt changed
 * (storage-adapter writeStudies), so a stored legacy value converges when
 * that study is next actually edited — until then every load re-migrates
 * it in memory, which costs nothing.
 */

function migrateStudent(student: Student): Student {
  return {
    ...student,
    readingLevel: migrateReadingLevel(student.readingLevel),
    writingLevel: migrateWritingLevel(student.writingLevel),
  };
}

export function migrateGroupLevels(groups: StudyGroup[]): StudyGroup[] {
  return groups.map((group) => ({
    ...group,
    students: (group.students ?? []).map(migrateStudent),
  }));
}

export function migrateStudyLevels(studies: Study[]): Study[] {
  return studies.map((study) => ({
    ...study,
    assignments: (study.assignments ?? []).map((assignment) => ({
      ...assignment,
      student: migrateStudent(assignment.student),
    })),
  }));
}
